/**
 * Board ordering keys (doc 04 · `groups.position`, `columns.position`, `items.position`).
 *
 * Positions are zero-padded decimal strings so that lexicographic ordering in SQL is numeric
 * ordering, and so inserting between two neighbours is an O(1) write instead of renumbering a
 * 20k-item group. Fresh rows are spaced `POSITION_STEP` apart, leaving ~20 halvings of room;
 * when two neighbours get closer than that, `needsRebalance` tells the service layer to
 * renumber the affected group.
 */
export const POSITION_WIDTH = 20;
export const POSITION_STEP = 1000000n;
const MAX = 10n ** BigInt(POSITION_WIDTH) - 1n;
function pad(value) {
    if (value <= 0n)
        throw new Error('position must be positive');
    if (value > MAX)
        throw new Error('position overflow: rebalance the group');
    return value.toString().padStart(POSITION_WIDTH, '0');
}
function toBigInt(position) {
    if (!/^\d+$/.test(position))
        throw new Error(`invalid position "${position}"`);
    return BigInt(position);
}
/** The position of the n-th row of a freshly built list (0-based). */
export function positionAt(index) {
    return pad(BigInt(index + 1) * POSITION_STEP);
}
/** Evenly spaced positions for a whole list — used by seeds, imports and rebalancing. */
export function positionSequence(count) {
    return Array.from({ length: count }, (_, index) => positionAt(index));
}
/**
 * A position strictly between `prev` and `next`. `null` means "start of list" / "end of list",
 * so `positionBetween(null, null)` is the first row and `positionBetween(last, null)` appends.
 */
export function positionBetween(prev, next) {
    if (prev === null && next === null)
        return pad(POSITION_STEP);
    if (prev === null) {
        const upper = toBigInt(next);
        if (upper <= 1n)
            throw new Error('position underflow: rebalance the group');
        return pad(upper > POSITION_STEP ? upper - POSITION_STEP : upper / 2n);
    }
    if (next === null)
        return pad(toBigInt(prev) + POSITION_STEP);
    const lower = toBigInt(prev);
    const upper = toBigInt(next);
    if (lower >= upper)
        throw new Error(`positions out of order: ${prev} >= ${next}`);
    if (upper - lower <= 1n)
        throw new Error('no gap left: rebalance the group');
    return pad((lower + upper) / 2n);
}
/** True when `positionBetween` would fail for these neighbours. */
export function needsRebalance(prev, next) {
    if (prev === null || next === null)
        return false;
    return toBigInt(next) - toBigInt(prev) <= 1n;
}
export function comparePositions(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
//# sourceMappingURL=ordering.js.map